<template>
  <div class="simple-content-dialog" :class="{ mobile: isMobile }">
    <div v-if="loaderIcon" class="loader">
      <GamLoader v-bind="getGamLoader" />
    </div>

    <div v-if="title || message" class="info-wrapper">
      <div v-if="title" class="info-title" v-html="localize(title)" />
      <div v-if="message" class="info-description" v-html="localize(message)" />
    </div>

    <div v-if="slots.default" class="content-wrapper">
      <slot />
    </div>

    <div v-if="rememberChoice && !isDisabled" class="button-group checkbox">
      <GamSwitch v-bind="getRememberChoice" v-model:checked="rememberChoiceRef" />
    </div>

    <div v-if="action && !cancel && !isDisabled" class="button-group">
      <GamButton
        v-if="action"
        v-bind="getActionButton"
        :class="isLongActionLabel ? 'lightly-padded-button' : 'padded-button'"
      />
    </div>

    <div v-if="action && cancel && !isDisabled" class="button-group horizontal">
      <GamButton v-if="cancel" v-bind="getCancelButton" class="lightly-padded-button" />
      <GamButton v-if="action" v-bind="getActionButton" class="lightly-padded-button" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamButton from '@/views/components/GamButton.vue';
import GamLoader from '@/views/components/GamLoader.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamLoaderColor, GamLoaderSize } from '@/views/composables/constants/components/gamLoader.constants';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamLoaderType } from '@/views/composables/models/components/GamLoader';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import type { SimpleContentDialogType } from '@/views/composables/models/dialog.interface';
import { computed, ref, useSlots } from 'vue';
import GamSwitch from '../../GamSwitch.vue';

const props = defineProps<SimpleContentDialogType>();

const slots = useSlots();

const isLongActionLabel = computed(() => localize(props.action?.label || '').length > 15);
const getActionButton = computed((): GamButtonType => {
  return {
    ...props.action,
    isDisabled: props.isDisabled,
    onClick: (e) => {
      if (!props.action?.onClick) return;

      if (props.rememberChoice && rememberChoiceRef.value === true) {
        gambits.dialogRepository.suppressDialog(props.rememberChoice);
      }
      props.action?.onClick?.(e);
    },
    onMouseDown: (e) => {
      props.action?.onMouseDown?.(e);
    },
    onMouseUp: (e) => {
      props.action?.onMouseUp?.(e);
    },
    variant: GamButtonVariant.SECONDARY,
    size: GamButtonSize.SMALL,
    isCentered: true,
  };
});

const getCancelButton = computed((): GamButtonType => {
  return {
    ...props.cancel,
    isDisabled: props.isDisabled,
    onClick: (e) => {
      if (props.rememberChoice && rememberChoiceRef.value === true) {
        gambits.dialogRepository.suppressDialog(props.rememberChoice);
      }
      props.cancel?.onClick?.(e);
    },
    variant: GamButtonVariant.AFFORDED,
    size: GamButtonSize.SMALL,
    isCentered: true,
  };
});

const rememberChoiceRef = ref<boolean>(false);
const getRememberChoice = computed(
  (): GamSwitchType => ({
    type: GamSwitchValueType.CHECKBOX,
    position: GamSwitchPosition.LEFT,
    label: 'dialog.remember_choice',
    name: 'rememberChoice',
  }),
);

const getGamLoader = computed((): GamLoaderType => {
  return {
    icon: props.loaderIcon,
    size: GamLoaderSize.LARGE,
    color: GamLoaderColor.GRADIENT,
    spinIcon: false,
  };
});
</script>

<style lang="scss" scoped>
.simple-content-dialog {
  display: flex;
  position: relative;
  width: calc(335px - 2 * var(--spacing-menu));
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-xl);

  &.mobile {
    width: 100%;
  }
}

.info-description {
  line-height: 1.5 !important;
}

.info-wrapper,
.content-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-l);
  align-self: stretch;
}

.info-title {
  font-size: 24px;
  font-style: normal;
  font-weight: 800;
  line-height: 110%;
  color: var(--color-white-100);
  text-align: center;
  align-self: stretch;
}

.info-description {
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 110%;
  color: var(--color-white-70);
  text-align: center;
  z-index: 2;

  span {
    color: var(--color-white-100);
  }

  a {
    color: var(--color-white-100);
  }
}

.content-wrapper {
  gap: 0;
}

.button-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &.checkbox {
    align-self: center;
    padding-top: 0;

    label {
      color: var(--color-white-30);
      font-size: 14px;
    }
  }

  &.horizontal {
    flex-direction: row;
    gap: var(--spacing-l);
  }
}

.padded-button :deep(.gam-button-container) {
  padding-left: var(--spacing-xxl) !important;
  padding-right: var(--spacing-xxl) !important;
}

.lightly-padded-button :deep(.gam-button-container) {
  padding-left: var(--spacing-xl) !important;
  padding-right: var(--spacing-xl) !important;
}

.info-description {
  :deep(span) {
    color: var(--color-white-100);
  }

  :deep(a) {
    color: var(--color-white-100);
  }
}
</style>
